<template>
  <div class="users">
    <OmxList
        :title="$t('top-menu.users')"
        :data="filteredUsersData"
        :loading="isLoading"
        :search-value.sync="search"
        :empty-icon="hasActiveFilters ? 'filter-variant' : 'account-group-outline'"
        :empty-title="hasActiveFilters ? $t('omx-list.empty-title') : $t('users-empty.title')"
        :empty-body="hasActiveFilters ? null : $t('users-empty.body')"
        :skeleton-columns="[1.6, 1.6, 1, 0.8]"
    >
      <template #chips>
        <b-select v-model="selectedRole" size="is-small" :placeholder="$t('users-view.user-role-placeholder')">
          <option v-for="option in userRoles" :value="option.id" :key="option.id">
            {{ $t(option.translationKey) }}
          </option>
        </b-select>
        <b-switch v-model="isOnlyArchived" size="is-small">
          {{ $t('users-view.show-archived-users-filter') }}
        </b-switch>
      </template>

      <template #empty-actions>
        <b-button v-if="hasActiveFilters" @click="resetFilters">{{ $t('omx-list.clear-selection') }}</b-button>
      </template>

      <b-table-column field="name" :label="$t('users-view.user-full-name-column')" v-slot="props" sortable>
        <a :href="userDetailsLink(props.row.user.document_id)" class="omx-row-title">
          {{ props.row.name }}
        </a>
      </b-table-column>

      <b-table-column field="user.email" :label="$t('users-view.user-email-column')" v-slot="props">
        <span class="omx-mono">{{ props.row.user.email }}</span>
      </b-table-column>

      <b-table-column field="role" :label="$t('users-view.user-role-column')" v-slot="props" sortable>
        <span class="omx-role-pill" :class="`omx-role-pill--${roleTone(props.row.role)}`">
          {{ roleLabel(props.row.role) }}
        </span>
      </b-table-column>

      <b-table-column :visible="isOnlyArchived" field="is_archived" :label="$t('users-view.archived-column')" v-slot="props" centered>
        <b-icon v-if="props.row.user.is_archived" icon="check" type="is-primary" />
      </b-table-column>
    </OmxList>
  </div>
</template>

<script>
import firebase from "firebase/app";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "Users",
  components: { OmxList },
  data() {
    return {
      usersData: [],
      isLoading: false,
      isOnlyArchived: false,
      selectedRole: "all",
      search: ""
    };
  },
  watch: {
    isOnlyArchived() { this.readUsersData(); },
    selectedRole() { this.readUsersData(); }
  },
  computed: {
    userRoles() {
      const all = this.userRoleOptions.slice();
      all.unshift({ id: "all", translationKey: "enums.user-role.all" });
      return all;
    },
    hasActiveFilters() {
      return this.search.trim() !== "" || this.selectedRole !== "all" || this.isOnlyArchived;
    },
    filteredUsersData() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.usersData;
      return this.usersData.filter(row => {
        return [row.name, row.user.email, this.roleLabel(row.role)]
            .filter(Boolean).join(" ").toLowerCase().includes(q);
      });
    }
  },
  methods: {
    roleLabel(role) {
      const opt = this.userRoleOptions.find(o => o.id === role);
      return opt ? this.$t(opt.translationKey) : role;
    },
    roleTone(role) {
      switch (role) {
        case 'system':          return 'system';
        case 'admin':           return 'admin';
        case 'project_manager': return 'pm';
        case 'site_manager':    return 'sm';
        case 'inactive':        return 'warn';
        default:                return 'neutral';
      }
    },
    resetFilters() {
      this.search = "";
      this.selectedRole = "all";
      this.isOnlyArchived = false;
    },
    readUsersData() {
      this.isLoading = true;
      this.usersData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getUsersFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {
        action: "get_users",
        fill_meta: true,
        filters: [['is_archived', '==', this.isOnlyArchived]]
      };
      if (this.selectedRole !== 'all') {
        payload.filters.push(['roles', 'array_contains', this.selectedRole]);
      }
      getUsersFunction(payload)
          .then((result) => {
            result.data["users"].forEach((user) => {
              this.usersData.push({
                user,
                name: this.getFullName(user.name),
                role: user.roles[0]
              });
            });
            this.isLoading = false;
          })
          .catch((error) => {
            this.showError(error.message);
            this.isLoading = false;
          });
    },
    userDetailsLink(userId) {
      return "#/users/" + userId;
    }
  },
  mounted() {
    if (!this.currentUserAccess("Users", 'VIEW')) this.$router.back();
    this.readUsersData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.users {
  // OmxList provides its own surface.
}
.omx-row-title {
  color: $omx-brand-600;
  font-weight: 600;
  &:hover { color: $omx-brand-700; text-decoration: underline; }
}
.omx-mono {
  font-family: $omx-font-mono;
  font-size: 12.5px;
  color: $omx-ink-500;
}
.omx-role-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 999px;
  background: $omx-ink-100;
  color: $omx-ink-700;
  &--system { background: #efe9fb; color: #6f3ed4; }
  &--admin  { background: $omx-brand-100; color: $omx-brand-700; }
  &--pm     { background: $omx-success-100; color: $omx-success-600; }
  &--sm     { background: $omx-accent-100; color: $omx-accent-600; }
  &--warn   { background: $omx-warn-100; color: $omx-warn-600; }
}
</style>
